import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NavigationService } from 'src/app/services/navigation.service';
import { PickupData } from 'src/app/models/pickup/pickup-data.model';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ConfirmFormData } from 'src/app/models/confirm-data/confirm-form-data.model';
import { FormService } from 'src/app/services/form.service';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { ScreenType, SposobNadaniaType } from 'src/app/enums/enums';
import { PickupService } from 'src/app/services/pickup.service';
import { ApiService } from 'src/app/services/api.service';
import { SubmitButtonComponent } from '../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../common/back-button/back-button.component';
import { PageHeaderComponent } from '../../common/page-header/page-header.component';
import { CaptchaDialogService } from 'src/app/services/captcha-dialog.service';
import { FormErrorMessageComponent } from '../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';
import { emailValidator } from 'src/app/common/validatos/validators';

@Component({
  selector: 'app-courier-shipping-confirmation-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    PageHeaderComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './courier-shipping-confirmation-page.component.html',
  styleUrl: './courier-shipping-confirmation-page.component.scss',
})
export class ShippingCourierConfirmationPageComponent implements OnInit {
  SposobNadaniaType = SposobNadaniaType;

  confirmDataForm!: FormGroup;
  submitException = signal(false);
  isSubmitLoading = signal(false);

  constructor(
    private http: HttpClient,
    private navigationService: NavigationService,
    private fb: FormBuilder,
    private formService: FormService,
    private pickupService: PickupService,
    private apiService: ApiService,
    private captchaDialogService: CaptchaDialogService,
    protected storeService: StoreService
  ) {
    this.initializeForm();
    this.confirmDataForm.valueChanges.subscribe(() => {
      this.saveFormDataToCache();
    });
  }

  ngOnInit() {
    this.setupConditionalValidators();
  }

  private setupConditionalValidators() {
    this.printLabelControl?.valueChanges.subscribe((value) => {
      if (value) {
        this.emailControl?.setValidators([
          Validators.required,
          emailValidator(),
        ]);
      } else {
        this.emailControl?.clearValidators();
      }
      this.emailControl?.updateValueAndValidity();
    });
  }

  //todo zaksa printLabelControl i emailControl do wyrzucenia.
  private initializeForm() {
    this.confirmDataForm = this.fb.group({
      print_the_label: [this.storeService.getConfirmFormData()?.printLabel],
      email: [this.storeService.getConfirmFormData()?.printLabelEmail],
      accept_processing_data: [
        {
          value: this.storeService.getConfirmFormData()?.acceptProcesingData,
          disabled: false,
        },
        [Validators.requiredTrue],
      ],
      confirm_data: [
        {
          value: this.storeService.getConfirmFormData()?.cofirmData,
          disabled: false,
        },
        [Validators.requiredTrue],
      ],
    });
    this.formService.markAllNotEmptyAsTouched(this.confirmDataForm);
  }

  get printLabelControl() {
    return this.confirmDataForm.get('print_the_label');
  }

  get emailControl() {
    return this.confirmDataForm.get('email');
  }

  get acceptProcessingDataControl() {
    return this.confirmDataForm.get('accept_processing_data');
  }

  get confirmDataControl() {
    return this.confirmDataForm.get('confirm_data');
  }

  isPayerNoProvided(): boolean {
    return this.storeService.getPayerNo() ? true : false;
  }

  saveFormDataToCache(): void {
    const confirmFormData: ConfirmFormData = {
      printLabel: this.printLabelControl?.value,
      printLabelEmail: this.emailControl?.value,
      acceptProcesingData: this.acceptProcessingDataControl?.value,
      cofirmData: this.confirmDataControl?.value,
    };

    this.storeService.setConfirmFormData(confirmFormData);
  }

  backButtonAction() {
    this.clearFields();
    this.navigationService.changeSposobNadaniaStepNumberTo(0);
  }

  clearFields() {
    this.submitException.set(false);
    this.acceptProcessingDataControl?.setValue(false, { emitEvent: false });
    this.confirmDataControl?.setValue(false, { emitEvent: false });
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.confirmDataForm.invalid) {
      this.formService.markAllAsTouched(this.confirmDataForm);
      this.isSubmitLoading.set(false);
      return;
    }

    try {
      const pickupData: PickupData = this.pickupService.buildPickupData();
      const pickupResponse = await firstValueFrom(
        this.apiService.submitPickupRequest(pickupData)
      );

      this.storeService.setPickupResponse(pickupResponse);
      this.clearFields();
      this.navigationService.changeSposobNadaniaStepNumberTo(2);
    } catch (error) {
      this.handlePickupError(error as HttpErrorResponse);
    } finally {
      this.isSubmitLoading.set(false);
    }
  }

  private handlePickupError(error: HttpErrorResponse) {
    try {
      const errorResponse: ErrorResponse = this.parseErrorResponse(error);

      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else {
        this.storeService.setPickupErrorResponse(errorResponse);
        this.navigateToProperStep(errorResponse);
      }
    } catch (e) {
      this.submitException.set(true);
    }
  }

  private parseErrorResponse(error: HttpErrorResponse): ErrorResponse {
    return {
      httpStatus: error.status,
      errorMsg: error.error.errorMsg.map((msg: any) => ({
        errorCode: msg.errorCode,
        field: msg.field,
        idRow: msg.idRow,
        errorMsgLabel: msg.errorMsgLabel,
        errorMsgArgs: msg.errorMsgArgs,
      })),
    };
  }

  private navigateToProperStep(errorResponse: ErrorResponse) {
    const createShipFields = [
      'items',
      'items.quantity',
      'item.length',
      'item.height',
      'item.compWeight',
      'item.width+item.length',
      'INS_VALUE',
      'INS_PCK_CONTENT',
      'COD_VALUE',
      'COD_ACCOUNT',
    ];
    const shippingOptionsFields: string[] = [];
    const deliveryOptionsFields = ['pckDate', 'pckInfo'];
    const shippingAndDeliveryFields = [
      'name',
      'companyName',
      'city',
      'address1',
      'phone',
      'email',
      'postalCode',
    ];
    const pointFields = ['ext2Id'];

    let backToCreateShip = false;
    let backToShippingOptions = false;
    let backToDeliveryOptions = false;

    errorResponse?.errorMsg?.forEach((err) => {
      if (createShipFields.includes(err.field)) backToCreateShip = true;
      if (shippingOptionsFields.includes(err.field))
        backToShippingOptions = true;
      if (deliveryOptionsFields.includes(err.field))
        backToDeliveryOptions = true;
      if (shippingAndDeliveryFields.includes(err.field)) {
        if (err.idRow === 2) backToDeliveryOptions = true;
        else if (err.idRow === 1) backToShippingOptions = true;
      }
      if (pointFields.includes(err.field)) {
        if (err.idRow === 2 || err.idRow === 3) backToDeliveryOptions = true;
        else if (err.idRow === 1) backToShippingOptions = true;
      }
    });

    if (backToCreateShip) {
      this.navigationService.changeSposobNadaniaStepNumberTo(0);
      this.navigationService.changeScreenTo(ScreenType.UTWORZ_PRZESYLKE);
    } else if (backToDeliveryOptions) {
      this.navigationService.changeSposobNadaniaStepNumberTo(0);
      this.navigationService.changeScreenTo(ScreenType.OPCJE_DORECZENIA);
    } else if (backToShippingOptions) {
      this.navigationService.changeSposobNadaniaStepNumberTo(0);
      this.navigationService.changeScreenTo(ScreenType.OPCJE_NADANIA);
    } else {
      this.submitException.set(true);
    }
  }
}
